<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useUserStore } from '../../stores/userStore'
import { useSongStore } from '../../stores/songStore'
import { useBandStore } from '../../stores/bandStore'
import { usePresenceStore } from '../../stores/presenceStore'
import { useUiStore } from '../../stores/uiStore'
import BandChat from './BandChat.vue'
import BandSubscriptionCard from './BandSubscriptionCard.vue'
import { useSetlistStore } from '../../stores/setlistStore'
import { useBandEventStore } from '../../stores/bandEventStore'
import type { BandDto, SongSummaryDto, SetlistSummaryDto, BandEventSummaryDto } from '../../dtos'
import api from '../../api'

const props = defineProps<{
  band: BandDto
}>()

const userStore = useUserStore()
const bandStore = useBandStore()
const songStore = useSongStore()
const presenceStore = usePresenceStore()
const uiStore = useUiStore()
const setlistStore = useSetlistStore()
const bandEventStore = useBandEventStore()

const isOwner = computed(() => {
  if (!userStore.dbUser) return false
  const me = props.band.members.find(m => m.userId === userStore.dbUser?.id)
  return me?.role === 'Owner'
})

const isOwnerOrAdmin = computed(() => {
  if (!userStore.dbUser) return false
  const me = props.band.members.find(m => m.userId === userStore.dbUser?.id)
  return me?.role === 'Owner' || me?.role === 'Admin'
})

const nonOwnerMembers = computed(() => props.band.members.filter(m => m.role !== 'Owner'))

// Leave band
const showLeaveConfirm = ref(false)
const isLeaving = ref(false)

const handleLeaveBand = async () => {
  isLeaving.value = true
  try {
    await bandStore.leaveBand(props.band.id)
    showLeaveConfirm.value = false
  } catch (error) {
    console.error(error)
  } finally {
    isLeaving.value = false
  }
}

// Transfer ownership
const showTransferDialog = ref(false)
const transferTargetUserId = ref<number | null>(null)
const showTransferConfirm = ref(false)
const isTransferring = ref(false)

const transferTargetName = computed(() => {
  if (!transferTargetUserId.value) return ''
  return props.band.members.find(m => m.userId === transferTargetUserId.value)?.displayName || 'Unknown'
})

const openTransferConfirm = () => {
  if (!transferTargetUserId.value) return
  showTransferConfirm.value = true
}

const handleTransferOwnership = async () => {
  if (!transferTargetUserId.value) return
  isTransferring.value = true
  try {
    await bandStore.transferOwnership(props.band.id, transferTargetUserId.value)
    showTransferConfirm.value = false
    showTransferDialog.value = false
    transferTargetUserId.value = null
  } catch (error) {
    console.error(error)
  } finally {
    isTransferring.value = false
  }
}

const shareLoading = ref(false)
const linkCopied = ref(false)

const handleShareLink = async () => {
  if (!userStore.user) return
  shareLoading.value = true
  try {
    const token = await userStore.user.getIdToken()
    const response = await api.post(`/band-invite-links/${props.band.id}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const inviteToken = response.data.token
    const url = `${window.location.origin}?invite=${inviteToken}`

    if (navigator.share) {
      await navigator.share({
        title: 'Join my band on RiffDraft!',
        text: `Join "${props.band.name}" on RiffDraft`,
        url
      })
    } else {
      await navigator.clipboard.writeText(url)
      linkCopied.value = true
      setTimeout(() => { linkCopied.value = false }, 2000)
    }
  } catch (error: any) {
    if (error.name !== 'AbortError') console.error('Share failed:', error)
  } finally {
    shareLoading.value = false
  }
}

const songs = computed(() => songStore.songs)
const newSongTitle = ref('')
const addingSong = ref(false)
const showDeleteConfirm = ref(false)
const songToDelete = ref<SongSummaryDto | null>(null)
const duplicatingId = ref<number | null>(null)
const importingFile = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)

const showDuplicateSongConfirm = ref(false)
const songToDuplicate = ref<SongSummaryDto | null>(null)

const confirmDuplicateSong = (song: SongSummaryDto) => {
  songToDuplicate.value = song
  showDuplicateSongConfirm.value = true
}

const handleDuplicateSong = async () => {
  if (!songToDuplicate.value) return
  duplicatingId.value = songToDuplicate.value.id
  try {
    await songStore.duplicateSong(songToDuplicate.value.id)
    showDuplicateSongConfirm.value = false
    songToDuplicate.value = null
  } catch (error) {
    console.error(error)
  } finally {
    duplicatingId.value = null
  }
}

const handleAddSong = async () => {
  if (!newSongTitle.value) return
  addingSong.value = true
  try {
    await songStore.addSong(newSongTitle.value)
    newSongTitle.value = ''
  } finally {
    addingSong.value = false
  }
}

const handleImportClick = () => {
  fileInput.value?.click()
}

const handleImportSong = async (event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  importingFile.value = true
  try {
    await songStore.importSong(file)
  } catch (error) {
    console.error('Failed to import song:', error)
  } finally {
    importingFile.value = false
    input.value = ''
  }
}

// Setlist Logic
const setlists = computed(() => setlistStore.setlists)
const newSetlistName = ref('')
const addingSetlist = ref(false)
const duplicatingSetlistId = ref<number | null>(null)
const showDeleteSetlistConfirm = ref(false)
const setlistToDelete = ref<SetlistSummaryDto | null>(null)

const handleAddSetlist = async () => {
  if (!newSetlistName.value) return
  addingSetlist.value = true
  try {
    await setlistStore.addSetlist(newSetlistName.value)
    newSetlistName.value = ''
  } finally {
    addingSetlist.value = false
  }
}

const showDuplicateSetlistConfirm = ref(false)
const setlistToDuplicate = ref<SetlistSummaryDto | null>(null)

const confirmDuplicateSetlist = (setlist: SetlistSummaryDto) => {
  setlistToDuplicate.value = setlist
  showDuplicateSetlistConfirm.value = true
}

const handleDuplicateSetlist = async () => {
  if (!setlistToDuplicate.value) return
  duplicatingSetlistId.value = setlistToDuplicate.value.id
  try {
    await setlistStore.duplicateSetlist(setlistToDuplicate.value.id)
    showDuplicateSetlistConfirm.value = false
    setlistToDuplicate.value = null
  } catch (error) {
    console.error(error)
  } finally {
    duplicatingSetlistId.value = null
  }
}

const confirmDeleteSetlist = (setlist: SetlistSummaryDto) => {
  setlistToDelete.value = setlist
  showDeleteSetlistConfirm.value = true
}

const handleDeleteSetlist = async () => {
  if (!setlistToDelete.value) return
  try {
    await setlistStore.deleteSetlist(setlistToDelete.value.id)
    showDeleteSetlistConfirm.value = false
    setlistToDelete.value = null
  } catch (error) {
    console.error(error)
  }
}

// Event Logic
const futureEvents = computed(() => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return bandEventStore.events.filter(e => new Date(e.eventDate) >= today)
})
const showEventDialog = ref(false)
const editingEventId = ref<number | null>(null)
const eventName = ref('')
const eventDate = ref<Date | null>(null)
const eventDuration = ref<number | null>(60)
const eventLocation = ref('')
const eventDescription = ref('')
const eventSetlistId = ref<number | null>(null)
const savingEvent = ref(false)
const creatingEventSetlist = ref(false)

const createEventSetlist = async () => {
  if (!eventDate.value || !eventName.value.trim()) return
  creatingEventSetlist.value = true
  try {
    const d = eventDate.value
    const dateStr = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(-2)}`
    const name = `${dateStr}-${eventName.value.trim()}`
    const created = await setlistStore.addSetlist(name)
    if (created) eventSetlistId.value = created.id
  } catch (e) {
    console.error('Failed to create event setlist:', e)
  } finally {
    creatingEventSetlist.value = false
  }
}
const showDeleteEventConfirm = ref(false)
const eventToDelete = ref<BandEventSummaryDto | null>(null)
const togglingEventId = ref<number | null>(null)
const eventDatePicker = ref<any>(null)

const closeDatePicker = () => {
  if (eventDatePicker.value) {
    eventDatePicker.value.overlayVisible = false
  }
}

const openNewEventDialog = () => {
  editingEventId.value = null
  eventName.value = ''
  eventDate.value = null
  eventDuration.value = 60
  eventLocation.value = ''
  eventDescription.value = ''
  eventSetlistId.value = null
  showEventDialog.value = true
}

const openEditEventDialog = async (event: BandEventSummaryDto) => {
  const details = await bandEventStore.getEventDetails(event.id)
  if (!details) return
  editingEventId.value = details.id
  eventName.value = details.name
  eventDate.value = new Date(details.eventDate)
  eventDuration.value = details.durationMinutes
  eventLocation.value = details.location || ''
  eventDescription.value = details.description || ''
  eventSetlistId.value = details.setlistId || null
  showEventDialog.value = true
}

const handleSaveEvent = async () => {
  if (!eventName.value || !eventDate.value) return
  savingEvent.value = true
  try {
    if (editingEventId.value) {
      await bandEventStore.updateEvent(editingEventId.value, {
        name: eventName.value,
        eventDate: eventDate.value.toISOString(),
        durationMinutes: eventDuration.value || 60,
        location: eventLocation.value || undefined,
        description: eventDescription.value || undefined,
        setlistId: eventSetlistId.value || undefined
      })
    } else {
      await bandEventStore.createEvent({
        name: eventName.value,
        eventDate: eventDate.value.toISOString(),
        durationMinutes: eventDuration.value || 60,
        location: eventLocation.value || undefined,
        description: eventDescription.value || undefined,
        setlistId: eventSetlistId.value || undefined
      })
    }
    showEventDialog.value = false
  } catch (error) {
    console.error(error)
  } finally {
    savingEvent.value = false
  }
}

const confirmDeleteEvent = (event: BandEventSummaryDto) => {
  eventToDelete.value = event
  showDeleteEventConfirm.value = true
}

const handleDeleteEvent = async () => {
  if (!eventToDelete.value) return
  try {
    await bandEventStore.deleteEvent(eventToDelete.value.id)
    showDeleteEventConfirm.value = false
    eventToDelete.value = null
  } catch (error) {
    console.error(error)
  }
}

const handleToggleAttendance = async (event: BandEventSummaryDto) => {
  togglingEventId.value = event.id
  try {
    await bandEventStore.toggleAttendance(event.id)
  } catch (error) {
    console.error(error)
  } finally {
    togglingEventId.value = null
  }
}

const formatEventDateTime = (dateStr: string) => {
  const d = new Date(dateStr)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const tomorrow = new Date(today.getTime() + 86400000)
  const eventDay = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })

  if (eventDay.getTime() === today.getTime()) return { label: 'Today', time, urgent: true }
  if (eventDay.getTime() === tomorrow.getTime()) return { label: 'Tomorrow', time, urgent: true }
  return { label: d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }), time, urgent: false }
}

const confirmDelete = async (song: SongSummaryDto) => {
  // Check concurrency before showing confirm dialog
  const isLocked = await presenceStore.checkConcurrency('Song', song.id)
  if (isLocked) {
    uiStore.triggerConcurrencyError('Cannot delete this song while other members are active in it or its mixes.')
    return
  }

  songToDelete.value = song
  showDeleteConfirm.value = true
}

const handleDelete = async () => {
  if (!songToDelete.value) return
  try {
    await songStore.deleteSong(songToDelete.value.id)
    showDeleteConfirm.value = false
    songToDelete.value = null
  } catch (error) {
    console.error(error)
  }
}

// Fetch songs, setlists, and events when band changes
watch(() => props.band.id, (newId) => {
  if (newId) {
    songStore.fetchBandSongs(newId)
    setlistStore.fetchBandSetlists(newId)
    bandEventStore.fetchBandEvents(newId)
  }
}, { immediate: true })

defineEmits(['invite-member', 'view-song', 'view-setlist', 'open-teleprompter'])
</script>

<template>
  <div class="space-y-4">
    <div class="p-6 rd-card">
      <!-- Active Band pulsing header -->
      <div class="flex items-center gap-2 mb-1">
        <div class="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></div>
        <p class="rd-label">Active Band</p>
      </div>
      <div class="flex items-center justify-between">
        <h2 class="text-2xl font-black text-slate-900">{{ band.name }}</h2>
        <div class="flex gap-2">
          <Button v-if="isOwnerOrAdmin" :label="linkCopied ? 'Link Copied!' : 'Invite People'" :icon="linkCopied ? 'pi pi-check' : 'pi pi-share-alt'" size="small" severity="secondary" outlined class="text-xs !border-slate-400" :loading="shareLoading" @click="handleShareLink" />
        </div>
      </div>
      
      <!-- Events Grid -->
      <div class="mt-4 pt-4 rd-divider grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Events Section -->
        <div class="max-w-[500px] lg:max-w-none">
          <div class="flex items-center justify-between mb-3">
            <p class="rd-label">Upcoming Events</p>
            <Button v-if="bandStore.canEditEvents" icon="pi pi-plus" size="small" severity="primary" text rounded v-tooltip.top="'New Event'" @click="openNewEventDialog" />
          </div>
          <div v-if="futureEvents.length > 0" class="flex flex-col gap-2 max-h-64 overflow-y-auto pr-1">
            <div v-for="event in futureEvents" :key="event.id"
                 class="group p-3 rounded-xl border border-slate-400 hover:border-teal-600 hover:bg-slate-50 hover:shadow-md hover:shadow-teal-500/5 transition-all"
                 :class="bandStore.canEditEvents ? 'cursor-pointer' : ''"
                 @click="bandStore.canEditEvents && openEditEventDialog(event)">
              <div class="flex items-center justify-between">
                <span class="text-sm font-bold text-slate-700 group-hover:text-slate-900">{{ event.name }}</span>
                <div class="flex items-center gap-1">
                  <button
                    class="attend-btn"
                    :class="{ 'is-attending': event.isCurrentUserAttending, 'is-toggling': togglingEventId === event.id }"
                    v-tooltip.top="event.isCurrentUserAttending ? 'You are attending this event' : 'You are not attending this event'"
                    @click.stop="handleToggleAttendance(event)">
                    <i :class="event.isCurrentUserAttending ? 'pi pi-check-circle' : 'pi pi-user-plus'"></i>
                    <span v-if="!event.isCurrentUserAttending" class="attend-label">Accept</span>
                  </button>
                  <Button v-if="event.setlistId" icon="pi pi-list" text rounded size="small"
                          class="opacity-60 hover:opacity-100 transition-opacity"
                          v-tooltip.top="'View setlist'"
                          @click.stop="$emit('view-setlist', { id: event.setlistId })" />
                  <Button v-if="bandStore.canEditEvents" icon="pi pi-trash" severity="danger" text rounded size="small"
                          class="opacity-60 hover:opacity-100 transition-opacity"
                          v-tooltip.top="'Delete event'"
                          @click.stop="confirmDeleteEvent(event)" />
                </div>
              </div>
              <div class="flex items-center gap-3 mt-1 text-xs text-slate-500">
                <span>
                  <i class="pi pi-calendar mr-1"></i>
                  <strong v-if="formatEventDateTime(event.eventDate).urgent" class="event-urgent-label">{{ formatEventDateTime(event.eventDate).label }}</strong>
                  <span v-else>{{ formatEventDateTime(event.eventDate).label }}</span>
                  {{ formatEventDateTime(event.eventDate).time }}
                </span>
                <span v-if="event.location"><i class="pi pi-map-marker mr-1"></i>{{ event.location }}</span>
              </div>
              <div class="mt-1 text-[9px] font-bold text-teal-600 uppercase tracking-tighter">
                <i class="pi pi-users mr-1"></i>{{ event.memberCount }}/{{ band.members.length }} attending
              </div>
            </div>
          </div>
          <div v-else class="py-8 text-center border-2 border-dashed border-slate-200 rounded-xl bg-slate-50/30">
            <i class="pi pi-calendar text-2xl text-slate-200 mb-2"></i>
            <p class="text-xs text-slate-400 italic">No upcoming events</p>
          </div>
        </div>

        <!-- Members Section (shown at top on lg+) -->
        <div class="hidden lg:block">
          <p class="rd-label mb-3">Members</p>
          <div class="flex flex-col gap-3">
            <div v-for="member in band.members" :key="member.userId" class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <img v-if="member.profilePicture" :src="member.profilePicture" class="w-8 h-8 rounded-full object-cover" @error="() => { member.profilePicture = undefined }" />
                <Avatar v-else :label="(member.displayName || '?').charAt(0)" shape="circle" size="small" class="text-[10px] w-6 h-6" />
                <div class="flex flex-col">
                  <span class="text-sm font-medium text-slate-700">{{ member.displayName || 'Unknown' }}</span>
                  <span v-if="member.role === 'Owner'" class="text-[8px] font-bold text-teal-600 uppercase">Owner</span>
                </div>
              </div>
              <span class="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase bg-slate-100 text-slate-500">
                {{ member.bandRole || member.role }}
              </span>
            </div>
          </div>
          <div class="mt-4 flex gap-2">
            <Button v-if="isOwner" label="Manage Members" icon="pi pi-users" size="small" severity="secondary" outlined class="text-xs !border-slate-400" @click="$emit('invite-member')" />
            <Button v-if="isOwner && nonOwnerMembers.length > 0" label="Transfer Band" icon="pi pi-arrow-right-arrow-left" size="small" severity="secondary" outlined class="text-xs !border-slate-400" @click="showTransferDialog = true" />
            <Button v-if="!isOwner" label="Leave Band" icon="pi pi-sign-out" size="small" severity="danger" outlined class="text-xs" @click="showLeaveConfirm = true" />
          </div>
        </div>
      </div>

      <!-- Songs Section -->
      <div class="mt-8 pt-4 rd-divider">
        <div class="flex items-center justify-between mb-4 max-w-[500px]">
          <p class="rd-label">Songs</p>
          <span class="text-[10px] font-bold text-slate-300">{{ songs.length }} Total</span>
        </div>

        <!-- Add Song Input -->
        <div v-if="bandStore.canEditSongs" class="flex gap-2 mb-4 max-w-[500px]">
          <Button icon="pi pi-file-import" size="small" severity="secondary" outlined class="!border-slate-400" :loading="importingFile" v-tooltip.top="'Import song from JSON or image'" @click="handleImportClick" />
          <input ref="fileInput" type="file" accept=".json,image/jpeg,image/png,image/webp,image/gif" class="hidden" @change="handleImportSong" />
          <InputText v-model="newSongTitle" placeholder="New song title..." class="flex-1 text-sm" size="small" @keyup.enter="handleAddSong" />
          <Button icon="pi pi-plus" size="small" severity="primary" :loading="addingSong" @click="handleAddSong" />
        </div>

        <!-- Songs List -->
        <div v-if="songs.length > 0" class="flex flex-col gap-1 max-h-64 overflow-y-auto pr-1 max-w-[500px]">
          <div v-for="song in songs" :key="song.id"
               class="group rd-list-row"
               @click="$emit('view-song', song)">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 rounded bg-slate-100 flex items-center justify-center text-slate-400 group-hover:bg-teal-50 group-hover:text-teal-600 transition-colors">
                <i class="pi pi-volume-up text-xs"></i>
              </div>
              <div class="flex flex-col">
                <span class="text-sm font-medium text-slate-700 group-hover:text-slate-900">{{ song.title }}</span>
                <div v-if="song.mixCount > 0" class="flex items-center gap-1 text-[9px] font-bold text-teal-600 uppercase tracking-tighter">
                  <i class="pi pi-headphones"></i>
                  <span>{{ song.mixCount }} Mix{{ song.mixCount > 1 ? 'es' : '' }}</span>
                </div>
              </div>
            </div>
            <div class="flex items-center gap-1">
              <Button icon="pi pi-clipboard" severity="secondary" text rounded size="small"
                      class="opacity-40 hover:opacity-100 transition-opacity"
                      v-tooltip.top="'Teleprompter'"
                      @click.stop="$emit('open-teleprompter', song.id)" />
              <template v-if="bandStore.canEditSongs">
                <Button icon="pi pi-copy" severity="secondary" text rounded size="small"
                        class="opacity-60 hover:opacity-100 transition-opacity"
                        :loading="duplicatingId === song.id"
                        v-tooltip.top="'Duplicate song'"
                        @click.stop="confirmDuplicateSong(song)" />
                <Button icon="pi pi-trash" severity="danger" text rounded size="small"
                        class="opacity-60 hover:opacity-100 transition-opacity"
                        v-tooltip.top="'Delete song'"
                        @click.stop="confirmDelete(song)" />
              </template>
            </div>
          </div>
        </div>
        <div v-else class="py-12 text-center border-2 border-dashed border-slate-200 rounded-xl bg-slate-50/30">
          <i class="pi pi-cloud-upload text-2xl text-slate-200 mb-2"></i>
          <p class="text-xs text-slate-400 italic">No songs added yet</p>
        </div>
      </div>

      <!-- Setlists Section -->
      <div class="mt-8 pt-4 rd-divider">
        <div class="flex items-center justify-between mb-4 max-w-[500px]">
          <p class="rd-label">Setlists</p>
          <span class="text-[10px] font-bold text-slate-300">{{ setlists.length }} Total</span>
        </div>

        <!-- Add Setlist Input -->
        <div v-if="bandStore.canEditSetlists" class="flex gap-2 mb-4 max-w-[500px]">
          <InputText v-model="newSetlistName" placeholder="New setlist name..." class="flex-1 text-sm" size="small" @keyup.enter="handleAddSetlist" />
          <Button icon="pi pi-plus" size="small" severity="primary" :loading="addingSetlist" @click="handleAddSetlist" />
        </div>

        <!-- Setlists List -->
        <div v-if="setlists.length > 0" class="flex flex-col gap-1 max-h-64 overflow-y-auto pr-1 max-w-[500px]">
          <div v-for="setlist in setlists" :key="setlist.id"
               class="group rd-list-row"
               @click="$emit('view-setlist', setlist)">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 rounded bg-slate-100 flex items-center justify-center text-slate-400 group-hover:bg-teal-50 group-hover:text-teal-600 transition-colors">
                <i class="pi pi-list text-xs"></i>
              </div>
              <div class="flex flex-col">
                <span class="text-sm font-medium text-slate-700 group-hover:text-slate-900">{{ setlist.name }}</span>
                <div v-if="setlist.songCount > 0" class="flex items-center gap-1 text-[9px] font-bold text-teal-600 uppercase tracking-tighter">
                  <i class="pi pi-music"></i>
                  <span>{{ setlist.songCount }} Song{{ setlist.songCount > 1 ? 's' : '' }}</span>
                </div>
              </div>
            </div>
            <div v-if="bandStore.canEditSetlists" class="flex items-center gap-1">
              <Button icon="pi pi-copy" severity="secondary" text rounded size="small"
                      class="opacity-60 hover:opacity-100 transition-opacity"
                      :loading="duplicatingSetlistId === setlist.id"
                      v-tooltip.top="'Duplicate setlist'"
                      @click.stop="confirmDuplicateSetlist(setlist)" />
              <Button icon="pi pi-trash" severity="danger" text rounded size="small"
                      class="opacity-60 hover:opacity-100 transition-opacity"
                      v-tooltip.top="'Delete setlist'"
                      @click.stop="confirmDeleteSetlist(setlist)" />
            </div>
          </div>
        </div>
        <div v-else class="py-12 text-center border-2 border-dashed border-slate-200 rounded-xl bg-slate-50/30">
          <i class="pi pi-list text-2xl text-slate-200 mb-2"></i>
          <p class="text-xs text-slate-400 italic">No setlists created yet</p>
        </div>
      </div>

      <!-- Band Chat -->
      <BandChat :band-id="band.id" />

      <!-- Members Section (mobile/tablet — shown at top on lg+) -->
      <div class="mt-8 pt-4 rd-divider lg:hidden">
        <div class="max-w-[500px]">
          <p class="rd-label mb-3">Members</p>
          <div class="flex flex-col gap-3">
            <div v-for="member in band.members" :key="member.userId" class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <img v-if="member.profilePicture" :src="member.profilePicture" class="w-8 h-8 rounded-full object-cover" @error="() => { member.profilePicture = undefined }" />
                <Avatar v-else :label="(member.displayName || '?').charAt(0)" shape="circle" size="small" class="text-[10px] w-6 h-6" />
                <div class="flex flex-col">
                  <span class="text-sm font-medium text-slate-700">{{ member.displayName || 'Unknown' }}</span>
                  <span v-if="member.role === 'Owner'" class="text-[8px] font-bold text-teal-600 uppercase">Owner</span>
                </div>
              </div>
              <span class="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase bg-slate-100 text-slate-500">
                {{ member.bandRole || member.role }}
              </span>
            </div>
          </div>
          <div class="mt-4 flex gap-2">
            <Button v-if="isOwner" label="Manage Members" icon="pi pi-users" size="small" severity="secondary" outlined class="text-xs !border-slate-400" @click="$emit('invite-member')" />
            <Button v-if="isOwner && nonOwnerMembers.length > 0" label="Transfer Band" icon="pi pi-arrow-right-arrow-left" size="small" severity="secondary" outlined class="text-xs !border-slate-400" @click="showTransferDialog = true" />
            <Button v-if="!isOwner" label="Leave Band" icon="pi pi-sign-out" size="small" severity="danger" outlined class="text-xs" @click="showLeaveConfirm = true" />
          </div>
        </div>
      </div>

      <!-- Subscription / tier / credits -->
      <BandSubscriptionCard :band-id="band.id" />
    </div>

    <!-- Song Delete Confirmation Dialog -->
    <Dialog v-model:visible="showDeleteConfirm" modal :closable="false" :style="{ width: '22rem' }">
      <div class="flex flex-col items-center text-center py-6">
        <div class="w-32 h-32 bg-red-50 rounded-full flex items-center justify-center mb-6">
          <i class="pi pi-exclamation-triangle text-red-500" style="font-size: 5rem"></i>
        </div>
        <h2 class="text-xl font-bold text-slate-900 mb-8">Are you sure?</h2>
        <p class="text-sm text-slate-500 mb-8 px-4">This will permanently delete the song <strong>"{{ songToDelete?.title }}"</strong> and all its associated parts, mixes, and audio files.</p>
        
        <div class="flex flex-col gap-3 w-full">
          <Button label="Yes, Delete Song" severity="danger" class="w-full py-3 font-bold" @click="handleDelete" />
          <Button label="Cancel" severity="secondary" text class="w-full font-medium" @click="showDeleteConfirm = false" />
        </div>
      </div>
    </Dialog>

    <!-- Song Duplicate Confirmation Dialog -->
    <Dialog v-model:visible="showDuplicateSongConfirm" modal :closable="false" :style="{ width: '22rem' }">
      <div class="flex flex-col items-center text-center py-6">
        <div class="w-32 h-32 bg-teal-50 rounded-full flex items-center justify-center mb-6">
          <i class="pi pi-copy text-teal-500" style="font-size: 5rem"></i>
        </div>
        <h2 class="text-xl font-bold text-slate-900 mb-8">Duplicate Song?</h2>
        <p class="text-sm text-slate-500 mb-8 px-4">This will create a copy of <strong>"{{ songToDuplicate?.title }}"</strong> including all its parts.</p>

        <div class="flex flex-col gap-3 w-full">
          <Button label="Yes, Duplicate" severity="success" class="w-full py-3 font-bold" :loading="duplicatingId === songToDuplicate?.id" @click="handleDuplicateSong" />
          <Button label="Cancel" severity="secondary" text class="w-full font-medium" @click="showDuplicateSongConfirm = false" />
        </div>
      </div>
    </Dialog>

    <!-- Event Editor Dialog -->
    <Dialog v-model:visible="showEventDialog" modal :header="editingEventId ? 'Edit Event' : 'New Event'" :style="{ width: '28rem' }">
      <div class="flex flex-col gap-4 py-2">
        <div class="flex flex-col gap-2">
          <label for="eventName" class="font-semibold text-sm">Event Name</label>
          <InputText id="eventName" v-model="eventName" class="w-full" placeholder="e.g. Rehearsal, Gig..." />
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div class="flex flex-col gap-2">
            <label for="eventDate" class="font-semibold text-sm">Date & Time</label>
            <DatePicker ref="eventDatePicker" id="eventDate" v-model="eventDate" showTime hourFormat="24" dateFormat="dd/mm/yy" class="w-full" placeholder="Pick date...">
              <template #footer>
                <div class="p-2 border-t border-slate-200 flex justify-end">
                  <Button label="Done" size="small" severity="success" @click="closeDatePicker" />
                </div>
              </template>
            </DatePicker>
          </div>
          <div class="flex flex-col gap-2">
            <label for="eventDuration" class="font-semibold text-sm">Duration (min)</label>
            <InputNumber id="eventDuration" v-model="eventDuration" class="w-full" :useGrouping="false" :min="15" :step="15" fluid />
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <label for="eventLocation" class="font-semibold text-sm">Location</label>
          <InputText id="eventLocation" v-model="eventLocation" class="w-full" placeholder="e.g. Studio, Venue..." />
        </div>
        <div class="flex flex-col gap-2">
          <label for="eventDescription" class="font-semibold text-sm">Description</label>
          <Textarea id="eventDescription" v-model="eventDescription" class="w-full" rows="3" placeholder="Notes about the event..." />
        </div>
        <div class="flex flex-col gap-2">
          <div class="flex items-center justify-between">
            <label class="font-semibold text-sm">Event Setlist</label>
            <Button v-if="!eventSetlistId" icon="pi pi-plus" text rounded size="small" :loading="creatingEventSetlist" v-tooltip.top="'Create setlist for this event'" @click="createEventSetlist" />
          </div>
          <Select v-model="eventSetlistId" :options="setlists" optionLabel="name" optionValue="id" placeholder="None" showClear class="w-full" />
        </div>
        <div class="flex justify-end gap-2 mt-4">
          <Button type="button" label="Cancel" severity="secondary" text @click="showEventDialog = false" />
          <Button type="button" :label="editingEventId ? 'Save Changes' : 'Create Event'" :loading="savingEvent" @click="handleSaveEvent" />
        </div>
      </div>
    </Dialog>

    <!-- Event Delete Confirmation Dialog -->
    <Dialog v-model:visible="showDeleteEventConfirm" modal :closable="false" :style="{ width: '22rem' }">
      <div class="flex flex-col items-center text-center py-6">
        <div class="w-32 h-32 bg-red-50 rounded-full flex items-center justify-center mb-6">
          <i class="pi pi-exclamation-triangle text-red-500" style="font-size: 5rem"></i>
        </div>
        <h2 class="text-xl font-bold text-slate-900 mb-8">Are you sure?</h2>
        <p class="text-sm text-slate-500 mb-8 px-4">This will permanently delete the event <strong>"{{ eventToDelete?.name }}"</strong>.</p>
        <div class="flex flex-col gap-3 w-full">
          <Button label="Yes, Delete Event" severity="danger" class="w-full py-3 font-bold" @click="handleDeleteEvent" />
          <Button label="Cancel" severity="secondary" text class="w-full font-medium" @click="showDeleteEventConfirm = false" />
        </div>
      </div>
    </Dialog>

    <!-- Setlist Delete Confirmation Dialog -->
    <Dialog v-model:visible="showDeleteSetlistConfirm" modal :closable="false" :style="{ width: '22rem' }">
      <div class="flex flex-col items-center text-center py-6">
        <div class="w-32 h-32 bg-red-50 rounded-full flex items-center justify-center mb-6">
          <i class="pi pi-exclamation-triangle text-red-500" style="font-size: 5rem"></i>
        </div>
        <h2 class="text-xl font-bold text-slate-900 mb-8">Are you sure?</h2>
        <p class="text-sm text-slate-500 mb-8 px-4">This will permanently delete the setlist <strong>"{{ setlistToDelete?.name }}"</strong>.</p>

        <div class="flex flex-col gap-3 w-full">
          <Button label="Yes, Delete Setlist" severity="danger" class="w-full py-3 font-bold" @click="handleDeleteSetlist" />
          <Button label="Cancel" severity="secondary" text class="w-full font-medium" @click="showDeleteSetlistConfirm = false" />
        </div>
      </div>
    </Dialog>

    <!-- Setlist Duplicate Confirmation Dialog -->
    <Dialog v-model:visible="showDuplicateSetlistConfirm" modal :closable="false" :style="{ width: '22rem' }">
      <div class="flex flex-col items-center text-center py-6">
        <div class="w-32 h-32 bg-teal-50 rounded-full flex items-center justify-center mb-6">
          <i class="pi pi-copy text-teal-500" style="font-size: 5rem"></i>
        </div>
        <h2 class="text-xl font-bold text-slate-900 mb-8">Duplicate Setlist?</h2>
        <p class="text-sm text-slate-500 mb-8 px-4">This will create a copy of <strong>"{{ setlistToDuplicate?.name }}"</strong>.</p>

        <div class="flex flex-col gap-3 w-full">
          <Button label="Yes, Duplicate" severity="success" class="w-full py-3 font-bold" :loading="duplicatingSetlistId === setlistToDuplicate?.id" @click="handleDuplicateSetlist" />
          <Button label="Cancel" severity="secondary" text class="w-full font-medium" @click="showDuplicateSetlistConfirm = false" />
        </div>
      </div>
    </Dialog>

    <!-- Transfer Ownership Dialog -->
    <Dialog v-model:visible="showTransferDialog" modal header="Transfer Band Ownership" :style="{ width: '25rem' }">
      <div class="flex flex-col gap-4 py-2">
        <p class="text-sm text-slate-500">Select a member to transfer ownership of <strong>"{{ band.name }}"</strong> to.</p>
        <Select v-model="transferTargetUserId" :options="nonOwnerMembers" optionLabel="displayName" optionValue="userId" placeholder="Select a member..." class="w-full" />
        <div class="flex justify-end gap-2 mt-4">
          <Button label="Cancel" severity="secondary" text @click="showTransferDialog = false" />
          <Button label="Transfer Ownership" severity="warning" :disabled="!transferTargetUserId" @click="openTransferConfirm" />
        </div>
      </div>
    </Dialog>

    <!-- Transfer Ownership Confirm Dialog -->
    <Dialog v-model:visible="showTransferConfirm" modal :closable="false" :style="{ width: '22rem' }">
      <div class="flex flex-col items-center text-center py-6">
        <div class="w-32 h-32 bg-amber-50 rounded-full flex items-center justify-center mb-6">
          <i class="pi pi-exclamation-triangle text-amber-500" style="font-size: 5rem"></i>
        </div>
        <h2 class="text-xl font-bold text-slate-900 mb-8">Transfer Ownership?</h2>
        <p class="text-sm text-slate-500 mb-8 px-4">This will make <strong>{{ transferTargetName }}</strong> the new owner of <strong>"{{ band.name }}"</strong>. You will remain as a member with all edit permissions disabled.</p>
        <div class="flex flex-col gap-3 w-full">
          <Button label="Yes, Transfer Ownership" severity="warning" class="w-full py-3 font-bold" :loading="isTransferring" @click="handleTransferOwnership" />
          <Button label="Cancel" severity="secondary" text class="w-full font-medium" @click="showTransferConfirm = false" />
        </div>
      </div>
    </Dialog>

    <!-- Leave Band Confirmation Dialog -->
    <Dialog v-model:visible="showLeaveConfirm" modal :closable="false" :style="{ width: '22rem' }">
      <div class="flex flex-col items-center text-center py-6">
        <div class="w-32 h-32 bg-red-50 rounded-full flex items-center justify-center mb-6">
          <i class="pi pi-sign-out text-red-500" style="font-size: 5rem"></i>
        </div>
        <h2 class="text-xl font-bold text-slate-900 mb-4">Leave Band?</h2>
        <p class="text-sm text-slate-500 mb-8 px-4">Are you sure you want to leave <strong>"{{ band.name }}"</strong>? You will need a new invite to rejoin.</p>
        <div class="flex flex-col gap-3 w-full">
          <Button label="Yes, Leave Band" severity="danger" class="w-full py-3 font-bold" :loading="isLeaving" @click="handleLeaveBand" />
          <Button label="Cancel" severity="secondary" text class="w-full font-medium" @click="showLeaveConfirm = false" />
        </div>
      </div>
    </Dialog>
  </div>
</template>

<style>
.attend-btn {
  height: 2rem;
  border-radius: 9999px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0 0.5rem;
  transition: background 0.2s;
}

.attend-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #94a3b8;
  transition: color 0.2s;
}

.attend-btn:hover .attend-label {
  color: #0f172a;
}

.attend-btn .pi {
  font-size: 0.875rem;
  color: #94a3b8;
  transition: color 0.2s;
}

.attend-btn:hover {
  background: rgba(100, 116, 139, 0.3);
}

.attend-btn:hover .pi {
  color: #0f172a;
}

.attend-btn.is-attending {
  animation: attend-radiate 1.5s ease-out infinite;
}

.attend-btn.is-attending .pi {
  color: #0d9488;
}

.attend-btn.is-attending:hover {
  background: rgba(13, 148, 136, 0.1);
}

.attend-btn.is-attending:hover .pi {
  color: #14b8a6;
}

.attend-btn.is-toggling {
  pointer-events: none;
  opacity: 0.5;
}

@keyframes attend-radiate {
  0% {
    box-shadow: 0 0 0 0 rgba(13, 148, 136, 0.5);
  }
  100% {
    box-shadow: 0 0 0 8px rgba(13, 148, 136, 0);
  }
}

.event-urgent-label {
  color: #0f172a;
}

.dark-mode .event-urgent-label {
  color: #ffffff;
}
</style>
